<template>
  <base-modal :show="showModal" @controleModalApperance="$emit('closeModal')">
    <template #modal>
      <div class="modal_card">
        <div class="modal_header">
          <h5>
            {{ isEdit ? $t("chatbotFaqs.edit") : $t("chatbotFaqs.create") }}
          </h5>
          <button class="close_btn" @click="$emit('closeModal')">
            <i class="fal fa-times"></i>
          </button>
        </div>

        <div class="modal_body">
          <div class="faq_form_wrapper">
            <form @submit.prevent="submitForm">
              <div class="row">
                <!-- Start:: Question AR -->
                <base-input
                  col="12"
                  type="text"
                  :placeholder="$t('chatbotFaqs.questionAr')"
                  v-model.trim="formData.question_ar"
                  required
                />
                <!-- End:: Question AR -->

                <!-- Start:: Answer AR -->
                <div class="col-12 mb-3">
                  <label class="d-block mb-2"
                    >{{ $t("chatbotFaqs.answerAr") }} *</label
                  >
                  <textarea
                    class="form-control"
                    rows="4"
                    v-model="formData.answer_ar"
                    required
                  ></textarea>
                </div>
                <!-- End:: Answer AR -->

                <!-- Start:: Question EN -->
                <base-input
                  col="12"
                  type="text"
                  :placeholder="$t('chatbotFaqs.questionEn')"
                  v-model.trim="formData.question_en"
                  required
                />
                <!-- End:: Question EN -->

                <!-- Start:: Answer EN -->
                <div class="col-12 mb-3">
                  <label class="d-block mb-2"
                    >{{ $t("chatbotFaqs.answerEn") }} *</label
                  >
                  <textarea
                    class="form-control"
                    rows="4"
                    v-model="formData.answer_en"
                    required
                  ></textarea>
                </div>
                <!-- End:: Answer EN -->

                <!-- Start:: Tags -->
                <div class="col-12 mb-3">
                  <label class="d-block mb-2">{{
                    $t("chatbotFaqs.tags")
                  }}</label>
                  <div class="tags_input_wrapper">
                    <div class="tags_list mb-2" v-if="formData.tags.length">
                      <span
                        v-for="(tag, index) in formData.tags"
                        :key="index"
                        class="tag_item"
                      >
                        {{ tag }}
                        <i class="fal fa-times" @click="removeTag(index)"></i>
                      </span>
                    </div>
                    <div class="input-group">
                      <input
                        type="text"
                        class="form-control"
                        :placeholder="$t('chatbotFaqs.enterTag')"
                        v-model="newTag"
                        @keyup.enter="addTag"
                      />
                      <button
                        class="btn btn-outline-secondary"
                        type="button"
                        @click="addTag"
                      >
                        {{ $t("chatbotFaqs.addTag") }}
                      </button>
                    </div>
                  </div>
                </div>
                <!-- End:: Tags -->

                <!-- Start:: Is Active -->
                <div class="col-12 mb-3">
                  <base-switch-input
                    :optionsList="[
                      { id: true, name: $t('chatbotFaqs.active') },
                      { id: false, name: $t('chatbotFaqs.inactive') },
                    ]"
                    v-model="formData.is_active"
                  />
                </div>
                <!-- End:: Is Active -->
              </div>

              <!-- Start:: Submit Button -->
              <div class="form_submit_btn_wrapper">
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="isSubmitting"
                >
                  {{ $t("chatbotFaqs.save") }}
                </button>
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click="$emit('closeModal')"
                >
                  {{ $t("chatbotFaqs.cancel") }}
                </button>
              </div>
              <!-- End:: Submit Button -->
            </form>
          </div>
        </div>
      </div>
    </template>
  </base-modal>
</template>

<script>
import BaseModal from "@/components/ui/BaseModal.vue";

export default {
  name: "ChatbotFaqForm",

  components: {
    BaseModal,
  },

  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    faqData: {
      type: Object,
      default: null,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Start:: Form Data
      formData: {
        question_ar: "",
        answer_ar: "",
        question_en: "",
        answer_en: "",
        tags: [],
        is_active: true,
      },
      newTag: "",
      isSubmitting: false,
      // End:: Form Data
    };
  },

  watch: {
    faqData: {
      handler(newVal) {
        if (newVal && this.isEdit) {
          this.formData.question_ar = newVal.question_ar || "";
          this.formData.answer_ar = newVal.answer_ar || "";
          this.formData.question_en = newVal.question_en || "";
          this.formData.answer_en = newVal.answer_en || "";
          this.formData.tags = newVal.tags ? [...newVal.tags] : [];
          this.formData.is_active = newVal.is_active;
        } else if (!this.isEdit) {
          this.resetForm();
        }
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    // Start:: Add Tag
    addTag() {
      if (
        this.newTag.trim() &&
        !this.formData.tags.includes(this.newTag.trim())
      ) {
        this.formData.tags.push(this.newTag.trim());
        this.newTag = "";
      }
    },
    // End:: Add Tag

    // Start:: Remove Tag
    removeTag(index) {
      this.formData.tags.splice(index, 1);
    },
    // End:: Remove Tag

    // Start:: Submit Form
    async submitForm() {
      this.isSubmitting = true;
      try {
        const payload = new FormData();
        payload.append("question[ar]", this.formData.question_ar);
        payload.append("answer[ar]", this.formData.answer_ar);
        payload.append("question[en]", this.formData.question_en);
        payload.append("answer[en]", this.formData.answer_en);
        payload.append("is_active", this.formData.is_active ? 1 : 0);

        this.formData.tags.forEach((tag) => {
          payload.append("tags[]", tag);
        });

        if (this.isEdit) {
          payload.append("_method", "PATCH");
          await this.$axios({
            method: "POST",
            url: `chatbot-faqs/${this.faqData.id}`,
            data: payload,
          });
          this.$message.success(this.$t("chatbotFaqs.updateSuccess"));
        } else {
          await this.$axios({
            method: "POST",
            url: "chatbot-faqs",
            data: payload,
          });
          this.$message.success(this.$t("chatbotFaqs.createSuccess"));
        }

        this.$emit("refreshData");
        this.$emit("closeModal");
        this.resetForm();
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("errorOccurred")
        );
      } finally {
        this.isSubmitting = false;
      }
    },
    // End:: Submit Form

    // Start:: Reset Form
    resetForm() {
      this.formData = {
        question_ar: "",
        answer_ar: "",
        question_en: "",
        answer_en: "",
        tags: [],
        is_active: true,
      };
      this.newTag = "";
    },
    // End:: Reset Form
  },
};
</script>

<style scoped lang="scss">
.modal_card {
  background: white;
  border-radius: 12px;
  max-width: 800px;
  width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .modal_header {
    padding: 20px 25px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h5 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }

    .close_btn {
      background: none;
      border: none;
      font-size: 20px;
      cursor: pointer;
      color: #999;
      padding: 5px 10px;

      &:hover {
        color: #333;
      }
    }
  }

  .modal_body {
    overflow-y: auto;
    flex: 1;
  }
}

.faq_form_wrapper {
  padding: 20px;

  .tags_input_wrapper {
    .tags_list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .tag_item {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 5px 10px;
        background: #e3f2fd;
        border-radius: 4px;
        font-size: 13px;

        i {
          cursor: pointer;
          color: #999;

          &:hover {
            color: #f44336;
          }
        }
      }
    }
  }

  .form_submit_btn_wrapper {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    margin-top: 20px;
  }
}
</style>
